/**
 * useDocBytes — fetch a document's raw bytes through the
 * /api/storage/download-file proxy and expose loading / error /
 * arrayBuffer + blob + (optionally) decoded-text shapes for renderer
 * consumption.
 *
 * Renderers vary in what they need:
 *   - PdfViewer / DocxViewer / SheetViewer want the ArrayBuffer.
 *   - Image / Video want a blob: URL (cleanly revocable on unmount).
 *   - TextViewer wants a decoded string (encoding-sniffed: BOM-aware,
 *     UTF-8 with a replacement-char-run heuristic that flags likely-
 *     wrong encodings).
 *
 * Resilience (DV4):
 *   - `MAX_PREVIEW_BYTES` ceiling checked against `doc.sizeBytes`
 *     BEFORE the fetch — over the cap we never download; the hook
 *     returns `error: 'too-large'` (a typed code) so each viewer can
 *     show "File too large to preview — Download original" instead of
 *     OOM-ing the tab on a 500 MB file.
 *   - `AbortController` so closing the dialog actually aborts the
 *     in-flight request, plus a hard timeout → `error: 'timeout'`.
 *   - The text decode only runs when `wantsText` is set (the text
 *     viewer), and only under a smaller text-byte cap.
 *
 * The blob URL is created lazily on fetch success and revoked when the
 * hook unmounts (or `rawStoragePath` changes) — deferred to a
 * macrotask so an in-flight render still holding the old `src` doesn't
 * flash broken.
 */
import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Download, FileText, Upload } from 'lucide-react';
import { authedFetch } from '../../../lib/authedFetch';
import { Button } from '../../ui/button';
import { downloadDocument } from '../../../lib/documentDownload';
import { toast } from 'sonner';
import type { SiteDocument } from '../../../types/siteRag';
import type { DocumentBinaryLoader } from '../../../lib/siteDocumentOriginalClient';

/** Hard ceiling on what we'll download for an inline preview. Above
 *  this, every viewer falls back to "Download original". 25 MB covers
 *  the overwhelming majority of real docs/sheets/PDFs while keeping a
 *  single tab safe from OOM. */
export const MAX_PREVIEW_BYTES = 25 * 1024 * 1024;

/** Smaller cap on what we'll attempt to UTF-8-decode for the text
 *  viewer — decoding 25 MB on the main thread would jank badly, and
 *  TextViewer truncates the *display* to ~500 KB anyway. */
const MAX_TEXT_DECODE_BYTES = 4 * 1024 * 1024;

/** Hard wall-clock timeout for the download. */
const FETCH_TIMEOUT_MS = 60_000;

/** Typed error codes — viewers branch on these to pick the right
 *  recovery copy. A free-form string is also allowed for unexpected
 *  failures. */
export type DocBytesErrorCode = 'too-large' | 'timeout' | 'not-persisted' | 'fetch-failed';

export interface DocBytesError {
  code: DocBytesErrorCode;
  message: string;
}

export interface DocBytes {
  loading: boolean;
  error: DocBytesError | null;
  arrayBuffer: ArrayBuffer | null;
  blobUrl: string | null;
  text: string | null;
  /** True when the UTF-8 decode produced a suspicious run of U+FFFD
   *  replacement chars — the file is probably not UTF-8. Only ever set
   *  when the consumer passed `wantsText`. */
  textEncodingSuspect: boolean;
}

interface Options {
  /** Decode the bytes to text (BOM-aware). Only the text viewer needs
   *  this; everyone else skips the decode entirely. */
  wantsText?: boolean;
}

const IDLE: DocBytes = {
  loading: true, error: null, arrayBuffer: null, blobUrl: null, text: null, textEncodingSuspect: false,
};

/** Sniff a UTF-16 / UTF-8 BOM and decode accordingly; otherwise
 *  decode as UTF-8 and flag if the output has a high U+FFFD density. */
function decodeText(buf: ArrayBuffer): { text: string; suspect: boolean } {
  const bytes = new Uint8Array(buf);
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return { text: new TextDecoder('utf-8').decode(buf), suspect: false };
  }
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return { text: new TextDecoder('utf-16le').decode(buf), suspect: false };
  }
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return { text: new TextDecoder('utf-16be').decode(buf), suspect: false };
  }
  const text = new TextDecoder('utf-8', { fatal: false }).decode(buf);
  // Count U+FFFD (�). A handful is normal (stray bytes); a high
  // density means the file is almost certainly a non-UTF-8 encoding.
  let fffd = 0;
  for (let i = 0; i < text.length; i++) if (text.charCodeAt(i) === 0xfffd) fffd++;
  const suspect = text.length > 0 && fffd / text.length > 0.02; // >2% replacement chars
  return { text, suspect };
}

export function useDocBytes(doc: SiteDocument, opts: Options = {}): DocBytes {
  const { binaryLoader } = React.useContext(DocViewerActionsContext);
  const wantsText = !!opts.wantsText;
  const [state, setState] = useState<DocBytes>(IDLE);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState(IDLE);

    if (!doc.rawStoragePath) {
      setState({
        ...IDLE,
        loading: false,
        error: {
          code: 'not-persisted',
          message: 'This document was uploaded before raw-file persistence — re-upload to enable preview.',
        },
      });
      return;
    }

    // Refuse to even start a download for an oversized file.
    if (typeof doc.sizeBytes === 'number' && doc.sizeBytes > MAX_PREVIEW_BYTES) {
      setState({
        ...IDLE,
        loading: false,
        error: {
          code: 'too-large',
          message: `This file is ${(doc.sizeBytes / (1024 * 1024)).toFixed(1)} MB — too large to preview here. Download the original instead.`,
        },
      });
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    (async () => {
      try {
        const blob = binaryLoader ? await binaryLoader(doc, controller.signal) : await (async () => {
          const res = await authedFetch(
            `/api/storage/download-file?path=${encodeURIComponent(doc.rawStoragePath!)}`,
            { signal: controller.signal },
          );
          if (!res.ok) throw new Error(`Download failed (${res.status})`);
          return res.blob();
        })();

        // Second-line defence: if Content-Length lied (or sizeBytes was
        // stale) and the blob is over the cap, bail before buffering.
        if (blob.size > MAX_PREVIEW_BYTES) {
          if (cancelled) return;
          setState({
            ...IDLE,
            loading: false,
            error: {
              code: 'too-large',
              message: `This file is ${(blob.size / (1024 * 1024)).toFixed(1)} MB — too large to preview here. Download the original instead.`,
            },
          });
          return;
        }

        const arrayBuffer = await blob.arrayBuffer();
        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;

        let text: string | null = null;
        let textEncodingSuspect = false;
        if (wantsText && arrayBuffer.byteLength <= MAX_TEXT_DECODE_BYTES) {
          const decoded = decodeText(arrayBuffer);
          text = decoded.text;
          textEncodingSuspect = decoded.suspect;
        } else if (wantsText) {
          // Decode just the leading slice so the viewer can still show
          // *something*; TextViewer truncates the display anyway.
          const decoded = decodeText(arrayBuffer.slice(0, MAX_TEXT_DECODE_BYTES));
          text = decoded.text;
          textEncodingSuspect = decoded.suspect;
        }

        if (cancelled) {
          URL.revokeObjectURL(blobUrl);
          blobUrlRef.current = null;
          return;
        }
        setState({ loading: false, error: null, arrayBuffer, blobUrl, text, textEncodingSuspect });
      } catch (e) {
        if (cancelled) return;
        const aborted = e instanceof DOMException && e.name === 'AbortError';
        setState({
          ...IDLE,
          loading: false,
          error: aborted
            ? { code: 'timeout', message: 'Loading the file timed out. Try again, or download the original.' }
            : { code: 'fetch-failed', message: (e as Error)?.message ?? 'Failed to fetch document bytes' },
        });
      } finally {
        clearTimeout(timeoutId);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
      // Defer the revoke one macrotask so any in-flight render still
      // holding the old blob URL in an <img>/<video> src doesn't flash
      // a broken element before it unmounts.
      const url = blobUrlRef.current;
      blobUrlRef.current = null;
      if (url) setTimeout(() => URL.revokeObjectURL(url), 0);
    };
  }, [doc.id, doc.rawStoragePath, doc.contentHash, doc.sizeBytes, wantsText, binaryLoader]);

  return state;
}

/** Common loading + error shells reused by every renderer. Keeps
 *  the per-type renderers focused on the actual format. */
export const ViewerLoading: React.FC<{ label?: string }> = ({ label = 'Loading document…' }) => (
  <div className="h-full flex items-center justify-center text-text-muted text-xs" role="status" aria-live="polite">
    <div className="animate-pulse">{label}</div>
  </div>
);

export const ViewerError: React.FC<{ message: string }> = ({ message }) => (
  <div className="h-full flex items-center justify-center p-6" role="alert">
    <div className="rounded-md border border-danger/40 bg-danger/5 px-4 py-3 text-xs text-danger max-w-md text-center">
      {message}
    </div>
  </div>
);

/**
 * DocViewerActionsContext — lets a viewer (or the ViewerRecovery
 * panel) reach the dialog-level "view extracted text" opener without
 * threading a prop through the lazy ViewerComponent type. DocViewerDialog
 * is the provider. Default is a no-op so a viewer rendered outside the
 * dialog (e.g. in a test) just doesn't show that button.
 */
export interface DocViewerActions {
  binaryLoader?: DocumentBinaryLoader;
  onDownloadOriginal?: (doc: SiteDocument) => Promise<void>;
  /** Opens the extracted-text ("what the AI sees") view for a doc, or
   *  `null` when no such affordance is available in this host. */
  onViewExtractedText: ((doc: SiteDocument) => void) | null;
  /**
   * DVF-2 — re-extract this document. Surfaced by the ViewerRecovery
   * panel when the PDF can't render AND the doc has no extracted text
   * fallback (so "View extracted text" would dead-end). When null, the
   * affordance is hidden — preserves backwards compatibility with
   * hosts that haven't wired re-extraction (tests, legacy mounts).
   */
  onReExtract?: ((doc: SiteDocument) => void | Promise<void>) | null;
  /**
   * DVF-RU — re-upload this document's ORIGINAL file in place. Surfaced
   * by the ViewerRecovery panel only when the failure is `not-persisted`
   * (a legacy doc uploaded before raw-file persistence: it has no
   * `rawStoragePath`, so the bytes were never saved and there is nothing
   * to download / preview). The handler takes the user-picked `File`,
   * re-saves it under the SAME doc id/metadata so `rawStoragePath` gets
   * stamped, and persists the patch — turning the dead-end "re-upload to
   * enable preview" copy into a one-click fix. When null, the affordance
   * is hidden (preserves back-compat for hosts that haven't wired it —
   * tests, legacy mounts).
   */
  onReUpload?: ((doc: SiteDocument, file: File) => void | Promise<void>) | null;
}
export const DocViewerActionsContext = React.createContext<DocViewerActions>({
  onViewExtractedText: null,
  onReExtract: null,
  onReUpload: null,
});

/**
 * ViewerRecovery — the in-viewer escape-hatch panel. Shown when a
 * viewer can't render the file (too large, corrupt, unsupported codec)
 * but the file itself is fine to download / has extracted text. Mirrors
 * the DocViewerErrorBoundary fallback so the UX is consistent whether
 * the failure was a thrown error (boundary) or a handled state (this).
 */
export const ViewerRecovery: React.FC<{
  doc: SiteDocument;
  title: string;
  detail?: string;
  /** Optional override for the download action (defaults to the shared downloadDocument). */
  onDownload?: () => void;
  /**
   * DVF-RU — the typed error code that put us on this panel. Only used to
   * decide whether to surface the in-place "Re-upload to enable preview"
   * affordance: that affordance is meaningful ONLY for the `not-persisted`
   * case (a legacy doc that has no stored original bytes). For every other
   * failure (`too-large`, `timeout`, `fetch-failed`, a render crash) the
   * bytes DO exist server-side, so re-upload is the wrong remedy and we
   * keep showing Download / Re-extract. Optional — callers that don't pass
   * it never show the re-upload affordance (back-compat for non-PDF viewers
   * and tests).
   */
  errorCode?: DocBytesErrorCode;
}> = ({ doc, title, detail, onDownload, errorCode }) => {
  const { onViewExtractedText, onReExtract, onReUpload, onDownloadOriginal, binaryLoader } = React.useContext(DocViewerActionsContext);
  const hasText = !!doc.textContent?.trim();
  // DVF-2 — when re-extract is wired AND the doc has no text fallback,
  // surface "Re-extract this document" instead of a dead-end "View
  // extracted text (disabled)" button. The user can still see what the
  // AI has on file once extraction finishes.
  const showReExtract = !hasText && !!onReExtract && !!doc.rawStoragePath;
  const [reExtracting, setReExtracting] = React.useState(false);
  // DVF-RU — the in-place re-upload fix. Only when the doc genuinely has
  // no stored original (`not-persisted`) AND a re-upload handler is wired.
  // This turns the dead-end "uploaded before raw-file persistence —
  // re-upload to enable preview" copy into a one-click file picker that
  // re-saves THIS doc (same id/metadata) with its original bytes.
  const showReUpload = errorCode === 'not-persisted' && !doc.rawStoragePath && !!onReUpload;
  const [reUploading, setReUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  return (
    <div className="h-full w-full flex items-center justify-center p-6" role="alert" data-testid="doc-viewer-recovery">
      <div className="rounded-md border border-warning/40 bg-warning/5 px-6 py-8 text-center space-y-4 max-w-md">
        <AlertTriangle className="w-8 h-8 text-warning mx-auto" />
        <div className="space-y-1">
          <div className="text-sm font-bold text-text-primary">{title}</div>
          {detail && <p className="text-xs text-text-secondary">{detail}</p>}
        </div>
        <div className="flex flex-wrap gap-2 justify-center">
          {showReUpload && (
            <>
              {/* Hidden picker — the visible Button proxies the click so the
                  control matches the rest of the panel's styling. Re-pick the
                  same original file and we re-save it under this doc's id. */}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                data-testid="doc-viewer-recovery-reupload-input"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  // Reset the input so re-picking the same file fires onChange.
                  e.target.value = '';
                  if (!file || !onReUpload) return;
                  setReUploading(true);
                  try {
                    await onReUpload(doc, file);
                    toast.success('Original file saved — preview enabled.');
                  } catch (err) {
                    toast.error((err as Error).message || 'Re-upload failed');
                  } finally {
                    setReUploading(false);
                  }
                }}
              />
              <Button
                size="sm" className="text-xs"
                disabled={reUploading}
                title="Pick the original file again to enable preview and download for this document"
                onClick={() => fileInputRef.current?.click()}
                data-testid="doc-viewer-recovery-reupload"
              >
                <Upload className="w-4 h-4 mr-1" />
                {reUploading ? 'Uploading…' : 'Re-upload to enable preview'}
              </Button>
            </>
          )}
          <Button
            size="sm" className="text-xs"
            disabled={!doc.rawStoragePath}
            title={doc.rawStoragePath ? 'Download the original file' : 'Re-upload to enable download'}
            onClick={onDownload ?? (async () => {
              try { if (onDownloadOriginal) await onDownloadOriginal(doc); else await downloadDocument(doc, binaryLoader); }
              catch (e) { toast.error((e as Error).message || 'Download failed'); }
            })}
            data-testid="doc-viewer-recovery-download"
          >
            <Download className="w-4 h-4 mr-1" /> Download original
          </Button>
          {showReExtract && (
            <Button
              size="sm" variant="outline" className="text-xs"
              disabled={reExtracting}
              title="Re-run text extraction on this document — unblocks the 'AI text' tab"
              onClick={async () => {
                if (!onReExtract) return;
                setReExtracting(true);
                try {
                  await onReExtract(doc);
                  toast.success('Re-extracted document text.');
                } catch (e) {
                  toast.error((e as Error).message || 'Re-extraction failed');
                } finally {
                  setReExtracting(false);
                }
              }}
              data-testid="doc-viewer-recovery-reextract"
            >
              <FileText className="w-4 h-4 mr-1" />
              {reExtracting ? 'Re-extracting…' : 'Re-extract this document'}
            </Button>
          )}
          {!showReExtract && onViewExtractedText && (
            <Button
              size="sm" variant="outline" className="text-xs"
              disabled={!hasText}
              title={
                hasText
                  ? 'Show the extracted text the AI sees'
                  : doc.rawStoragePath
                    ? 'No extracted text available — re-extract this document to populate it.'
                    : 'No extracted text available — re-upload to enable re-extraction.'
              }
              onClick={() => onViewExtractedText(doc)}
              data-testid="doc-viewer-recovery-extracted"
            >
              <FileText className="w-4 h-4 mr-1" /> View extracted text
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
